import React, { useState, useEffect } from 'react';
import { CreditCard, CheckCircle, AlertCircle } from 'lucide-react';
import { collection, query, where, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useToast } from '../../contexts/ToastContext';
import { getLastSwipedCard } from '../../services/rfidService';
import Card from '../ui/Card';

interface RFIDAttendanceListenerProps {
    isActive?: boolean;
}

const RFIDAttendanceListener: React.FC<RFIDAttendanceListenerProps> = ({ isActive = true }) => {
    const { showSuccess, showError } = useToast();
    const [isListening, setIsListening] = useState(false);
    const [lastSwipedCard, setLastSwipedCard] = useState<string | null>(null);
    const [lastProcessedCard, setLastProcessedCard] = useState<string | null>(null);

    useEffect(() => {
        if (!isActive) return;

        setIsListening(true);
        const interval = setInterval(async () => {
            try {
                const cardNumber = await getLastSwipedCard();
                if (cardNumber && cardNumber !== lastProcessedCard) {
                    setLastSwipedCard(cardNumber);
                    await processCardSwipe(cardNumber);
                    setLastProcessedCard(cardNumber);
                }
            } catch (error) {
                console.error('Error checking for card swipe:', error);
            }
        }, 2000); // Check every 2 seconds

        return () => {
            clearInterval(interval);
            setIsListening(false);
        };
    }, [isActive, lastProcessedCard]);

    const processCardSwipe = async (cardNumber: string) => {
        try {
            // Find staff or teacher with this card number
            const [staffSnapshot, teachersSnapshot] = await Promise.all([
                getDocs(query(collection(db, 'staff'), where('cardNumber', '==', cardNumber))),
                getDocs(query(collection(db, 'teachers'), where('cardNumber', '==', cardNumber)))
            ]);

            let employee: any = null;
            let employeeType: 'staff' | 'teacher' = 'staff';

            if (!staffSnapshot.empty) {
                const doc = staffSnapshot.docs[0];
                employee = { id: doc.id, ...doc.data(), type: 'staff' };
                employeeType = 'staff';
            } else if (!teachersSnapshot.empty) {
                const doc = teachersSnapshot.docs[0];
                employee = { id: doc.id, ...doc.data(), type: 'teacher' };
                employeeType = 'teacher';
            } else {
                showError('Card Not Registered', 'This RFID card is not registered to any staff or teacher.');
                return;
            }

            // Get employee ID
            const employeeId = employeeType === 'staff' ? employee.staffId : employee.teacherId;
            if (!employeeId) {
                showError('Error', 'Employee ID not found');
                return;
            }

            // Mark attendance for today
            const today = new Date();
            const year = today.getFullYear().toString();
            const monthNames = [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
            ];
            const monthName = monthNames[today.getMonth()];
            const yearMonth = `${year}_${monthName}`;
            const day = today.getDate().toString();

            // Reference: attendance/{type}/{employeeId}/{yearMonth}
            const monthRef = doc(db, 'attendance', employeeType, employeeId, yearMonth);
            const monthSnap = await getDoc(monthRef);

            let monthData: any = {};
            if (monthSnap.exists()) {
                monthData = monthSnap.data();
            }

            // Check if attendance already marked for today
            if (monthData[day] && monthData[day].P) {
                showError('Already Marked', `${employee.firstName} ${employee.lastName} is already marked present for today.`);
                return;
            }

            // Get current time
            const now = new Date();
            const checkInTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;

            // Mark as present
            monthData[day] = {
                P: true,
                checkIn_time: checkInTime,
                checkout_time: null, // Will be set when they check out
            };

            // Save to Firestore
            await setDoc(monthRef, monthData, { merge: true });

            showSuccess(
                'Attendance Marked',
                `${employee.firstName} ${employee.lastName} marked present at ${checkInTime}`
            );
        } catch (error) {
            console.error('Error processing card swipe:', error);
            showError('Error', 'Failed to mark attendance. Please try again.');
        }
    };

    if (!isActive) return null;

    return (
        <Card className="p-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${isListening ? 'bg-green-100' : 'bg-gray-100'}`}>
                        <CreditCard className={`h-5 w-5 ${isListening ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                        <p className="text-sm font-medium text-gray-900">
                            {isListening ? 'RFID Attendance Active' : 'RFID Attendance Inactive'}
                        </p>
                        <p className="text-xs text-gray-500">
                            {isListening
                                ? 'Listening for card swipes...'
                                : 'Not listening for card swipes'}
                        </p>
                    </div>
                </div>
                {lastSwipedCard && (
                    <div className="flex items-center space-x-2 text-xs text-gray-600">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span>Last: {lastSwipedCard}</span>
                    </div>
                )}
            </div>
        </Card>
    );
};

export default RFIDAttendanceListener;

